import * as React from 'react';
import { TestProjectInputEditorStore } from '../../stores';
import TestProjectInputSelectionStore from '../../stores/TestProjectInputSelectionStore';
import { Observer, observer } from 'mobx-react-lite';
import { Select, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { TestProjectInputMapping } from '../../models/TestProjectInputEditorDraft';
import { ArrowRightOutlined } from '@ant-design/icons';

type Props = {
    store: TestProjectInputEditorStore;
};

function getEmptyTargetMessage(inputSelectionStore: TestProjectInputSelectionStore) {
    if (!inputSelectionStore.topicInputsType) {
        return 'Choose a target input type.';
    }

    if (inputSelectionStore.topicInputsType === 'Bindings' && !inputSelectionStore.selectedAppForBindings) {
        return 'Select an application to see its available bindings.';
    }

    if (
        inputSelectionStore.topicInputsType === 'Bindings' &&
        inputSelectionStore.currentApplicationInputGroups.length > 0 &&
        !inputSelectionStore.selectedInputGroupForBindings
    ) {
        return 'Select an input group to see its available bindings.';
    }

    return 'No target inputs are available. Choose another application or input group, or configure inputs first.';
}

const TestProjectTopicsMappingPanel: React.FC<Props> = ({ store }) => {
    const draft = store.mappingDraft;

    if (!draft) {
        return null;
    }

    const inputSelectionStore = draft.inputSelectionStore;

    if (!draft.mappings.length) {
        return null;
    }

    const columns: ColumnsType<TestProjectInputMapping> = [
        {
            title: 'Current input',
            key: 'sourceTopic',
            render: (text, record) => {
                return record.sourceTopic.name;
            }
        },
        {
            title: '',
            key: 'operation',
            render: () => <ArrowRightOutlined />
        },
        {
            title: 'Target input',
            key: 'targetTopic',
            render: (_, record) => {
                return (
                    <Observer>
                        {() => (
                            <Select
                                aria-label={`Target for ${record.sourceTopic.name}`}
                                placeholder="Select target input"
                                disabled={
                                    !store.canEdit ||
                                    inputSelectionStore.isLoadingMetadata ||
                                    !inputSelectionStore.mappingOptions.length
                                }
                                style={{ width: '250px' }}
                                filterOption={(input, option) =>
                                    option?.title && option.title.toLowerCase().includes(input.toLowerCase())
                                }
                                options={inputSelectionStore.mappingOptions.map(src => {
                                    return {
                                        label: src.groupName,
                                        options: src.items.map(i => ({
                                            title: i.name,
                                            label: i.name,
                                            value: i.id
                                        }))
                                    };
                                })}
                                showSearch
                                value={record.newValue}
                                onChange={value => store.changeMappingTarget(record.sourceTopic.topicId, value)}
                                allowClear
                            />
                        )}
                    </Observer>
                );
            }
        }
    ];

    return (
        <div className="topics-mapping-panel">
            {!inputSelectionStore.isLoadingMetadata &&
                !inputSelectionStore.metadataError &&
                !inputSelectionStore.mappingOptions.some(group => group.items.length > 0) && (
                    <Typography.Paragraph type="secondary">
                        {getEmptyTargetMessage(inputSelectionStore)}
                    </Typography.Paragraph>
                )}
            <Table
                dataSource={draft.mappings}
                columns={columns}
                pagination={false}
                rowKey={r => r.sourceTopic.topicId}
            />
        </div>
    );
};

export default observer(TestProjectTopicsMappingPanel);
